import { isImageFile, stripExtension } from "./imageOptimizerLogic";

export { isImageFile, stripExtension };

export type CropPreset =
  | "free"
  | "1:1"
  | "4:3"
  | "3:2"
  | "16:9"
  | "9:16"
  | "2:3";

export type CropRect = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type RgbColor = {
  r: number;
  g: number;
  b: number;
};

export type EditorAdjustments = {
  flipH: boolean;
  flipV: boolean;
  saturation: number;
  contrast: number;
  gamma: number;
  grain: number;
  sharpen: number;
  removeBackground: boolean;
  backgroundColor: RgbColor | null;
  backgroundTolerance: number;
};

export type EditorState = {
  crop: CropRect;
  aspectPreset: CropPreset;
  outputWidth: number;
  outputHeight: number;
  lockAspect: boolean;
} & EditorAdjustments;

export const CROP_PRESETS: { id: CropPreset; label: string; ratio: number | null }[] =
  [
    { id: "free", label: "Free", ratio: null },
    { id: "1:1", label: "1:1", ratio: 1 },
    { id: "4:3", label: "4:3", ratio: 4 / 3 },
    { id: "3:2", label: "3:2", ratio: 3 / 2 },
    { id: "16:9", label: "16:9", ratio: 16 / 9 },
    { id: "9:16", label: "9:16", ratio: 9 / 16 },
    { id: "2:3", label: "2:3", ratio: 2 / 3 },
  ];

export function defaultEditorState(
  width: number,
  height: number,
): EditorState {
  return {
    crop: { x: 0, y: 0, w: width, h: height },
    aspectPreset: "free",
    outputWidth: width,
    outputHeight: height,
    lockAspect: true,
    flipH: false,
    flipV: false,
    saturation: 100,
    contrast: 100,
    gamma: 1,
    grain: 0,
    sharpen: 0,
    removeBackground: false,
    backgroundColor: null,
    backgroundTolerance: 24,
  };
}

export function clampCrop(
  crop: CropRect,
  imgW: number,
  imgH: number,
): CropRect {
  const w = Math.max(1, Math.min(crop.w, imgW));
  const h = Math.max(1, Math.min(crop.h, imgH));
  const x = Math.max(0, Math.min(crop.x, imgW - w));
  const y = Math.max(0, Math.min(crop.y, imgH - h));
  return { x, y, w, h };
}

export function cropForAspect(
  preset: CropPreset,
  imgW: number,
  imgH: number,
): CropRect {
  const entry = CROP_PRESETS.find((p) => p.id === preset);
  if (!entry?.ratio) {
    return { x: 0, y: 0, w: imgW, h: imgH };
  }

  const ratio = entry.ratio;
  let w = imgW;
  let h = Math.round(w / ratio);
  if (h > imgH) {
    h = imgH;
    w = Math.round(h * ratio);
  }
  const x = Math.round((imgW - w) / 2);
  const y = Math.round((imgH - h) / 2);
  return { x, y, w, h };
}

function applyColorAdjustments(
  data: Uint8ClampedArray,
  saturation: number,
  contrast: number,
  gamma: number,
) {
  const sat = saturation / 100;
  const con = contrast / 100;
  const invGamma = 1 / Math.max(0.05, gamma);

  for (let i = 0; i < data.length; i += 4) {
    let r = data[i];
    let g = data[i + 1];
    let b = data[i + 2];

    r = 255 * Math.pow(r / 255, invGamma);
    g = 255 * Math.pow(g / 255, invGamma);
    b = 255 * Math.pow(b / 255, invGamma);

    r = (r - 128) * con + 128;
    g = (g - 128) * con + 128;
    b = (b - 128) * con + 128;

    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
    r = gray + (r - gray) * sat;
    g = gray + (g - gray) * sat;
    b = gray + (b - gray) * sat;

    data[i] = Math.round(Math.min(255, Math.max(0, r)));
    data[i + 1] = Math.round(Math.min(255, Math.max(0, g)));
    data[i + 2] = Math.round(Math.min(255, Math.max(0, b)));
  }
}

function applySharpen(data: Uint8ClampedArray, w: number, h: number, amount: number) {
  if (amount <= 0) return;
  const strength = amount / 100;
  const copy = new Uint8ClampedArray(data);
  const kernel = [0, -1, 0, -1, 5, -1, 0, -1, 0];

  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      for (let c = 0; c < 3; c++) {
        let sum = 0;
        let ki = 0;
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * w + (x + kx)) * 4 + c;
            sum += copy[idx] * kernel[ki++];
          }
        }
        const idx = (y * w + x) * 4 + c;
        data[idx] = Math.round(
          copy[idx] * (1 - strength) + sum * strength,
        );
      }
    }
  }
}

function applyGrain(data: Uint8ClampedArray, amount: number) {
  if (amount <= 0) return;
  const amp = (amount / 100) * 48;
  for (let i = 0; i < data.length; i += 4) {
    const n = (Math.random() - 0.5) * amp;
    data[i] = Math.min(255, Math.max(0, data[i] + n));
    data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + n));
    data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + n));
  }
}

function colorDistance(
  r: number,
  g: number,
  b: number,
  target: RgbColor,
): number {
  const dr = r - target.r;
  const dg = g - target.g;
  const db = b - target.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

function applyBackgroundRemoval(
  data: Uint8ClampedArray,
  target: RgbColor,
  tolerance: number,
) {
  const maxDist = (tolerance / 100) * 140;
  for (let i = 0; i < data.length; i += 4) {
    const dist = colorDistance(
      data[i],
      data[i + 1],
      data[i + 2],
      target,
    );
    if (dist <= maxDist) {
      data[i + 3] = 0;
    }
  }
}

export function rgbToHex(color: RgbColor): string {
  const hex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${hex(color.r)}${hex(color.g)}${hex(color.b)}`.toUpperCase();
}

export function sampleImageColor(
  img: HTMLImageElement,
  x: number,
  y: number,
): RgbColor {
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  const sx = Math.max(0, Math.min(img.naturalWidth - 1, Math.floor(x)));
  const sy = Math.max(0, Math.min(img.naturalHeight - 1, Math.floor(y)));
  ctx.drawImage(img, sx, sy, 1, 1, 0, 0, 1, 1);
  const pixel = ctx.getImageData(0, 0, 1, 1).data;
  return { r: pixel[0], g: pixel[1], b: pixel[2] };
}

export function clientToImageCoords(
  clientX: number,
  clientY: number,
  rect: DOMRect,
  imgW: number,
  imgH: number,
): { x: number; y: number } {
  const relX = (clientX - rect.left) / rect.width;
  const relY = (clientY - rect.top) / rect.height;
  return {
    x: Math.min(imgW - 1, Math.max(0, Math.floor(relX * imgW))),
    y: Math.min(imgH - 1, Math.max(0, Math.floor(relY * imgH))),
  };
}

export function renderEditedImage(
  source: CanvasImageSource & { width?: number; height?: number },
  state: EditorState,
): HTMLCanvasElement {
  const crop = clampCrop(state.crop, source.width as number, source.height as number);
  const outW = Math.max(1, state.outputWidth || crop.w);
  const outH = Math.max(1, state.outputHeight || crop.h);

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Canvas not supported");

  ctx.save();
  if (state.flipH || state.flipV) {
    ctx.translate(state.flipH ? outW : 0, state.flipV ? outH : 0);
    ctx.scale(state.flipH ? -1 : 1, state.flipV ? -1 : 1);
  }

  ctx.drawImage(source, crop.x, crop.y, crop.w, crop.h, 0, 0, outW, outH);
  ctx.restore();

  const needsPixels =
    (state.removeBackground && state.backgroundColor !== null) ||
    state.saturation !== 100 ||
    state.contrast !== 100 ||
    state.gamma !== 1 ||
    state.grain > 0 ||
    state.sharpen > 0;

  if (needsPixels) {
    const imageData = ctx.getImageData(0, 0, outW, outH);
    if (state.removeBackground && state.backgroundColor) {
      applyBackgroundRemoval(
        imageData.data,
        state.backgroundColor,
        state.backgroundTolerance,
      );
    }
    applyColorAdjustments(
      imageData.data,
      state.saturation,
      state.contrast,
      state.gamma,
    );
    applySharpen(imageData.data, outW, outH, state.sharpen);
    applyGrain(imageData.data, state.grain);
    ctx.putImageData(imageData, 0, 0);
  }

  return canvas;
}

export async function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Export failed"))),
      "image/png",
    );
  });
}

export async function loadImageFile(file: File) {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = "async";
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = url;
  });
  return { img, url, width: img.naturalWidth, height: img.naturalHeight };
}

export function displayScale(
  naturalW: number,
  naturalH: number,
  maxW: number,
  maxH: number,
) {
  return Math.min(1, maxW / naturalW, maxH / naturalH);
}
